import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Activity, Check, ChevronDown, Copy, RefreshCw } from 'lucide-react';
import platformMcpService from '../../../services/platformMcpService';
import { describeApiError } from '../../../utils/apiError';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { formatWhen } from '../RunDetail/runDetailFormat';
import { WINDOW_OPTIONS, getBrowserTz, toExclusiveEndDate } from '../UsageAnalytics/usageFormat';
import { ACTIVITY_STATUS_OPTIONS, PLATFORM_MCP_COPY } from '../../../constants/platformMcpCopy';
import PaginationBar from '../../PlatformAdmin/PaginationBar';
import InfoTip from '../InfoTip';
import adminStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './PlatformMcpActivity.module.scss';
import {
  appLabel,
  formatLatency,
  humanizeTool,
  rangeText,
  statusMeta,
  summaryText,
  userLabel,
} from './platformMcpFormat';

const PAGE_SIZE = 50;
const ACTIVITY_WINDOWS = WINDOW_OPTIONS.filter((o) =>
  ['24h', '7d', '30d', 'today', 'yesterday', 'mtd'].includes(o.value)
);
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const ReferenceCell = ({ reference }) => {
  const [copied, setCopied] = useState(false);
  if (!reference) return <span className={adminStyles.muted}>—</span>;
  const handleCopy = async () => {
    if (await copyTextToClipboard(reference)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <span className={styles.reference}>
      <Tooltip title={PLATFORM_MCP_COPY.referenceTip} arrow>
        <span className={styles.referenceText}>{reference}</span>
      </Tooltip>
      <IconButton size="small" onClick={handleCopy} aria-label="Copy reference" title="Copy reference">
        {copied ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
      </IconButton>
    </span>
  );
};

const ResultChip = ({ status }) => {
  const meta = statusMeta(status);
  if (!meta.tone) return <span>{meta.label}</span>;
  const chip = <span className={`${adminStyles.effBadge} ${adminStyles[meta.tone]}`}>{meta.label}</span>;
  return meta.tip ? (
    <Tooltip title={meta.tip} arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

/**
 * Platform MCP call log — one table for every tier (spec §5):
 *   user     → own calls (no user/company fields)
 *   company  → the company's users
 *   partner  → users of all the partner's companies (+ company picker)
 *   platform → everything (+ company, partner and user filters, full `error`)
 *
 * Props:
 *   scope              'user' | 'company' | 'partner' | 'platform'
 *   loadCompanyOptions () => Promise<[{id, name}]>; omit to hide the company picker
 *   companyLabelFn     (id, name) => string
 *   partners           [{id, name}] for the partner picker (platform only)
 *   showError          expandable row with the call's full `error` (platform only)
 */
const PlatformMcpActivityTable = ({
  scope,
  loadCompanyOptions = null,
  companyLabelFn = null,
  partners = null,
  showError = false,
}) => {
  const tz = useMemo(() => getBrowserTz(), []);
  const showUser = scope !== 'user';
  const showCompany = scope === 'partner' || scope === 'platform';
  const userFilterable = scope === 'platform';

  const [windowValue, setWindowValue] = useState('7d');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [status, setStatus] = useState('all');
  const [toolInput, setToolInput] = useState('');
  const tool = useDebouncedValue(toolInput.trim(), 400);
  const [companyId, setCompanyId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [userFilter, setUserFilter] = useState(null);
  // The page offset belongs to one set of filters: when they change, the
  // table starts again from the first page without a second fetch.
  const [paging, setPaging] = useState({ params: null, offset: 0 });

  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const requestId = useRef(0);

  const isCustomRange = !!(dateRange.start || dateRange.end);

  useEffect(() => {
    if (!loadCompanyOptions) return undefined;
    let cancelled = false;
    loadCompanyOptions()
      .then((list) => !cancelled && setCompanies(Array.isArray(list) ? list : []))
      .catch(() => !cancelled && setCompanies([]));
    return () => {
      cancelled = true;
    };
  }, [loadCompanyOptions]);

  const filterParams = useMemo(() => {
    const p = { tz, status: status === 'all' ? '' : status, tool };
    if (dateRange.start && dateRange.end) {
      p.start = dateRange.start;
      p.end = toExclusiveEndDate(dateRange.end);
    } else {
      p.window = windowValue;
    }
    if (loadCompanyOptions) p.company_id = companyId;
    if (partners) p.partner_id = partnerId;
    if (userFilterable && userFilter) p.user_id = userFilter.id;
    return p;
  }, [tz, status, tool, dateRange, windowValue, loadCompanyOptions, companyId, partners, partnerId, userFilterable, userFilter]);

  const offset = paging.params === filterParams ? paging.offset : 0;
  const setOffset = (n) => setPaging({ params: filterParams, offset: n });

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await platformMcpService.listActivity(scope, {
        ...filterParams,
        limit: PAGE_SIZE,
        offset,
      });
      if (id === requestId.current) setData(res || null);
    } catch (err) {
      if (id === requestId.current) setError(describeApiError(err, "Couldn't load activity."));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [scope, filterParams, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const calls = Array.isArray(data?.calls) ? data.calls : [];
  const total = data?.total ?? 0;

  const gridTemplateColumns = [
    '150px',
    showUser && 'minmax(120px, 1fr)',
    showCompany && 'minmax(110px, 0.9fr)',
    'minmax(120px, 1fr)',
    'minmax(130px, 1fr)',
    '90px',
    '80px',
    'minmax(140px, 1.4fr)',
    '150px',
    showError && '32px',
  ]
    .filter(Boolean)
    .join(' ');

  const companyName = (c) => {
    if (!c) return PLATFORM_MCP_COPY.unknown;
    return companyLabelFn ? companyLabelFn(c.id, c.name) : c.name || `Company #${c.id}`;
  };

  return (
    <Box className={styles.wrap}>
      <Box className={adminStyles.toolbar}>
        <Box className={adminStyles.filterGroup}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={isCustomRange ? 'custom' : windowValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') {
                  setDateRange({ start: daysAgoStr(7), end: todayStr() });
                } else {
                  setDateRange({ start: '', end: '' });
                  setWindowValue(v);
                }
              }}
            >
              {ACTIVITY_WINDOWS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom range…</MenuItem>
            </Select>
          </FormControl>
          {isCustomRange && (
            <>
              <TextField
                size="small"
                type="date"
                label="Start"
                InputLabelProps={{ shrink: true }}
                value={dateRange.start}
                onChange={(e) => setDateRange((r) => ({ ...r, start: e.target.value }))}
              />
              <TextField
                size="small"
                type="date"
                label="End"
                InputLabelProps={{ shrink: true }}
                value={dateRange.end}
                onChange={(e) => setDateRange((r) => ({ ...r, end: e.target.value }))}
              />
            </>
          )}

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Result</InputLabel>
            <Select label="Result" value={status} onChange={(e) => setStatus(e.target.value)}>
              {ACTIVITY_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Tool"
            placeholder="e.g. run_agent"
            value={toolInput}
            onChange={(e) => setToolInput(e.target.value)}
            sx={{ width: 170 }}
          />

          {partners && (
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Partner</InputLabel>
              <Select label="Partner" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                <MenuItem value="">All partners</MenuItem>
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {loadCompanyOptions && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Company</InputLabel>
              <Select label="Company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <MenuItem value="">All companies</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {companyLabelFn ? companyLabelFn(c.id, c.name) : c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {userFilter && (
            <Chip
              size="small"
              label={`User: ${userLabel(userFilter)}`}
              onDelete={() => setUserFilter(null)}
            />
          )}
        </Box>

        <Tooltip title="Refresh" arrow>
          <span>
            <IconButton size="small" className={adminStyles.subtleBtn} onClick={load} disabled={loading}>
              <RefreshCw size={17} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {data && (
        <Box className={styles.summary}>
          <strong>{summaryText(data.summary, total)}</strong>
          {data.range && <span className={adminStyles.muted}>{rangeText(data.range)}</span>}
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : calls.length === 0 && !error ? (
        <Box className={adminStyles.emptyState}>
          <Activity className={adminStyles.emptyIcon} size={44} />
          <Typography variant="body2">{PLATFORM_MCP_COPY.activityEmpty}</Typography>
        </Box>
      ) : calls.length > 0 ? (
        <Box className={adminStyles.tableCard} sx={{ opacity: loading ? 0.6 : 1 }}>
          <Box className={`${styles.row} ${styles.head}`} style={{ gridTemplateColumns }}>
            <span>Time</span>
            {showUser && <span>User</span>}
            {showCompany && <span>Company</span>}
            <span>App</span>
            <span>What it did</span>
            <span>Result</span>
            <span>Time taken</span>
            <span>Reason</span>
            <span className={styles.headCell}>
              Reference <InfoTip title={PLATFORM_MCP_COPY.referenceTip} size={12} />
            </span>
            {showError && <span />}
          </Box>
          {calls.map((c) => {
            const isOpen = expanded === c.id;
            const canExpand = showError && !!c.error;
            return (
              <Box key={c.id} className={styles.rowWrap}>
                <Box className={styles.row} style={{ gridTemplateColumns }}>
                  <Tooltip title={formatWhen(c.created_at)}>
                    <span>{c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</span>
                  </Tooltip>
                  {showUser && (
                    <span className={styles.ellipsis} title={c.user?.email || ''}>
                      {userFilterable && c.user ? (
                        <Tooltip title="Show only this user's calls" arrow>
                          <button type="button" className={styles.linkBtn} onClick={() => setUserFilter(c.user)}>
                            {userLabel(c.user)}
                          </button>
                        </Tooltip>
                      ) : (
                        userLabel(c.user)
                      )}
                    </span>
                  )}
                  {showCompany && <span className={styles.ellipsis}>{companyName(c.company)}</span>}
                  <span className={styles.ellipsis}>{appLabel(c.key)}</span>
                  <Tooltip title={c.tool || ''}>
                    <span className={styles.ellipsis}>{humanizeTool(c.tool)}</span>
                  </Tooltip>
                  <span>
                    <ResultChip status={c.status} />
                  </span>
                  <span>{formatLatency(c.latency_ms)}</span>
                  <span className={styles.reason}>{c.status === 'error' ? c.message || '' : ''}</span>
                  <ReferenceCell reference={c.reference} />
                  {showError && (
                    <span>
                      {canExpand && (
                        <IconButton
                          size="small"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Hide error detail' : 'Show error detail'}
                          onClick={() => setExpanded(isOpen ? null : c.id)}
                        >
                          <ChevronDown
                            size={16}
                            style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
                          />
                        </IconButton>
                      )}
                    </span>
                  )}
                </Box>
                {canExpand && (
                  <Collapse in={isOpen} unmountOnExit>
                    <Box className={styles.errorDetail}>
                      {c.upstream_status != null && (
                        <span className={adminStyles.muted}>Upstream status {c.upstream_status}</span>
                      )}
                      <pre>{typeof c.error === 'string' ? c.error : JSON.stringify(c.error, null, 2)}</pre>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>
      ) : null}

      {total > PAGE_SIZE && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="calls"
          onOffsetChange={setOffset}
        />
      )}
    </Box>
  );
};

export default PlatformMcpActivityTable;
